import React, { useState, useEffect } from 'react';

import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress,
  DialogContentText
} from '@mui/material';
import {
  Settings as SettingsIcon,
  PowerSettingsNew as PowerIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  RestartAlt as RestartIcon,
  PowerOff as ShutdownIcon,
  Schedule as ScheduleIcon,
  DnsOutlined as DnsOutlinedIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getAuthHeaders  } from '../../../utils/authUtils';
import { 
  loadServerConfig as loadServerConfigFromAPI,
  updateServerConfig,
  apiFetch,
  getApiBaseUrl
} from '../../../utils/apiConfig';

const AdminConfig = ({ 
  adminConfig, 
  setAdminConfig, 
  loading,
  saveAdminConfig
}) => {
  const { t } = useTranslation();

  const [serverConfig, setServerConfig] = useState({
    server_listen_ip: '0.0.0.0',
    server_listen_port: '25000',
    server_aet_title: 'REPORTING_SCP',
    use_approval_workflow: true
  });
  const [testResults, setTestResults] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [restartDialog, setRestartDialog] = useState(false);
  const [shutdownDialog, setShutdownDialog] = useState(false);
  const [restartLoading, setRestartLoading] = useState(false);
  const [shutdownLoading, setShutdownLoading] = useState(false);
  const [restartStage, setRestartStage] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [scheduleDialog, setScheduleDialog] = useState({ open: false, action: '', minutes: 5 });

  // Load server configuration
  useEffect(() => {
    loadServerConfig();
  }, []);

  const loadServerConfig = async () => {
    try {
      // Lade Server-Konfiguration über die zentrale API-Konfiguration (aus config.ini)
      const data = await loadServerConfigFromAPI();
      
      if (data) {
        console.log('Loaded server config from config.ini:', data);
        console.log('use_approval_workflow value:', data.use_approval_workflow, 'type:', typeof data.use_approval_workflow);
        
        const approvalWorkflow = data.use_approval_workflow === 'true' || 
                                  data.use_approval_workflow === 'True' || 
                                  data.use_approval_workflow === true;
        console.log('Converted to boolean:', approvalWorkflow);
        
        setServerConfig({
          server_listen_ip: data.server_listen_ip || '127.0.0.1',
          server_listen_port: data.server_listen_port || '25000',
          server_aet_title: data.server_aet_title || 'REPORTING_SCP',
          use_approval_workflow: approvalWorkflow
        });
      } else {
        // Fallback: direkter Aufruf der INI-basierten API
        const response = await apiFetch('admin/config/Server');
        
        if (response.ok) {
          const fallbackData = await response.json();
          const approvalWorkflow = fallbackData.use_approval_workflow === 'true' || 
                                    fallbackData.use_approval_workflow === 'True' || 
                                    fallbackData.use_approval_workflow === true;
          
          setServerConfig({
            server_listen_ip: fallbackData.listen_ip || '127.0.0.1',
            server_listen_port: fallbackData.listen_port || '25000',
            server_aet_title: fallbackData.aet_title || 'REPORTING_SCP',
            use_approval_workflow: approvalWorkflow
          });
        }
      }
    } catch (error) {
      console.error('Error loading server config from config.ini:', error);
    }
  };

  // Schütze vor undefined adminConfig
  if (!adminConfig) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Typography>{t('common.loading')}</Typography>
        </Paper>
      </Box>
    );
  }

  const handleServerConfigChange = (field, value) => {
    setServerConfig(prev => ({ ...prev, [field]: value }));
  };

  const testServerConfig = async () => {
    setTestLoading(true);
    setTestResults(null);
    
    try {
      const authHeaders = getAuthHeaders();
      const response = await apiFetch('admin/test-server-config', {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(serverConfig)
      });

      if (response.ok) {
        const result = await response.json();
        setTestResults(result);
        // Mitteilung nach 5 Sekunden ausblenden
        setTimeout(() => setTestResults(null), 5000);
      } else {
        setTestResults({ success: false, errors: ['Fehler beim Testen der config.ini Konfiguration'] });
        setTimeout(() => setTestResults(null), 5000);
      }
    } catch (error) {
      setTestResults({ success: false, errors: ['Netzwerkfehler beim Testen der config.ini'] });
      setTimeout(() => setTestResults(null), 5000);
    } finally {
      setTestLoading(false);
    }
  };

  const saveServerConfig = async () => {
    setSaveLoading(true);
    
    try {
      const authHeaders = getAuthHeaders();
      const response = await apiFetch('admin/save-server-config', {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(serverConfig)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Aktualisiere die zentrale API-Konfiguration mit der neuen Server-Konfiguration
          updateServerConfig(serverConfig);
          
          setSaveSuccess(true);
          // Erfolgs-Mitteilung nach 5 Sekunden ausblenden
          setTimeout(() => setSaveSuccess(false), 5000);
          
          // Bei Server-Änderungen ist Neustart erforderlich (config.ini)
          if (result.restart_required) {
            setRestartDialog(true);
          }
        }
      }
    } catch (error) {
      console.error('Error saving server config:', error);
    } finally {
      setSaveLoading(false);
    }
  };

  const restartServer = async () => {
    setRestartLoading(true);
    setRestartStage('restarting');
    
    // Markiere Restart als laufend um Token-Löschung zu verhindern
    localStorage.setItem('server_restart_in_progress', 'true');
    
    // Speichere Token für später
    const authHeaders = getAuthHeaders();
    
    try {
      const response = await apiFetch('admin/restart-server', {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setRestartStage('checking');
        
        // Warte bis Server wirklich nicht mehr erreichbar ist
        await new Promise(resolve => setTimeout(resolve, 8000));
        
        // Prüfe Server-Verfügbarkeit kontinuierlich
        let attempts = 0;
        const maxAttempts = 60; // 2 Minuten maximal warten
        
        const checkServerAvailability = async () => {
          while (attempts < maxAttempts) {
            attempts++;
            
            try {
              // Teste grundlegende Verfügbarkeit ohne Auth
              const basicResponse = await fetch(getApiBaseUrl() + '/', {
                method: 'GET',
                signal: AbortSignal.timeout(3000)
              });
              
              if (basicResponse.ok) {
                console.log('Server ist grundsätzlich wieder verfügbar');
                
                // Teste ob unser Token noch gültig ist
                try {
                  const authResponse = await apiFetch('auth/me', {
                    method: 'GET',
                    signal: AbortSignal.timeout(3000)
                  });
                  
                  if (authResponse.ok) {
                    console.log('Token ist noch gültig - Server vollständig verfügbar');
                    setRestartStage('ready');
                    
                    // Zeige grünen Haken für 5 Sekunden, dann schließe Dialog
                    setTimeout(() => {
                      setRestartDialog(false);
                      setRestartLoading(false);
                      setRestartStage('');
                      // Restart abgeschlossen - Flag entfernen
                      localStorage.removeItem('server_restart_in_progress');
                    }, 5000);
                    return true;
                  } else {
                    console.log('Token ist nicht mehr gültig');
                    setRestartStage('token_invalid');
                    
                    // Informiere Benutzer und leite zur Login-Seite
                    setTimeout(() => {
                      setRestartDialog(false);
                      setRestartLoading(false);
                      setRestartStage('');
                      // Restart abgeschlossen - Flag entfernen
                      localStorage.removeItem('server_restart_in_progress');
                      alert('Server wurde neu gestartet. Bitte melden Sie sich erneut an.');
                      window.location.href = '/';
                    }, 3000);
                    return true;
                  }
                } catch (authError) {
                  console.log('Auth-Test fehlgeschlagen, versuche weiter...');
                }
              }
            } catch (e) {
              // Server noch nicht verfügbar - das ist normal während Restart
              console.log(`Versuch ${attempts}: Server noch nicht verfügbar`);
            }
            
            // Warte 2 Sekunden zwischen den Versuchen
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
          return false;
        };
        
        const isAvailable = await checkServerAvailability();
        
        if (!isAvailable) {
          // Fallback nach Timeout
          console.log('Timeout erreicht - Server möglicherweise nicht verfügbar');
          setRestartStage('timeout');
          setTimeout(() => {
            setRestartDialog(false);
            setRestartLoading(false);
            setRestartStage('');
            // Restart abgeschlossen - Flag entfernen
            localStorage.removeItem('server_restart_in_progress');
            alert('Server-Restart dauert länger als erwartet. Bitte prüfen Sie manuell.');
          }, 3000);
        }
        
      } else {
        throw new Error('Restart request failed');
      }
    } catch (error) {
      console.error('Error restarting server:', error);
      setRestartLoading(false);
      setRestartStage('error');
      // Bei Fehler Dialog nach 3 Sekunden schließen
      setTimeout(() => {
        setRestartDialog(false);
        setRestartStage('');
        // Restart abgeschlossen - Flag entfernen
        localStorage.removeItem('server_restart_in_progress');
        alert('Fehler beim Server-Restart. Bitte versuchen Sie es erneut.');
      }, 3000);
    }
  };

  const shutdownServer = async () => {
    setShutdownLoading(true);
    
    try {
      const authHeaders = getAuthHeaders();
      const response = await apiFetch('admin/shutdown-server', {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Nach dem Herunterfahren Dialog schließen
        setTimeout(() => {
          setShutdownDialog(false);
          setShutdownLoading(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Error shutting down server:', error);
      setShutdownLoading(false);
    }
  };

  const openScheduleDialog = (action) => {
    setScheduleDialog({ open: true, action, minutes: 5 });
  };

  const closeScheduleDialog = () => {
    setScheduleDialog({ open: false, action: '', minutes: 5 });
  };

  const scheduleAction = async () => {
    try {
      const authHeaders = getAuthHeaders();
      const endpoint = scheduleDialog.action === 'restart' ? '/admin/schedule-restart' : '/admin/schedule-shutdown';
      
      const response = await apiFetch(endpoint.startsWith('/') ? endpoint.slice(1) : endpoint, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ minutes: scheduleDialog.minutes })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          alert(result.message); // Temporary - später durch Toast ersetzen
          closeScheduleDialog();
        } else {
          alert(result.message);
        }
      }
    } catch (error) {
      console.error('Error scheduling action:', error);
      alert('Fehler beim Planen der Aktion');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Hauptüberschrift */}
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DnsOutlinedIcon color="primary" />
        {t('admin.serverManagement.adminConfig')}
      </Typography>

      {/* Server-Konfiguration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SettingsIcon color="primary" />
          <Typography variant="h6">{t('admin.serverConfig.title')}</Typography>
        </Box>
        
        {/* Erfolgs-Mitteilung */}
        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Konfiguration ist gültig
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('admin.serverConfig.description')}
        </Typography>

        <TextField
          fullWidth
          label={t('admin.serverConfig.serverListenIp')}
          value={serverConfig.server_listen_ip}
          onChange={(e) => handleServerConfigChange('server_listen_ip', e.target.value)}
          helperText={t('admin.serverConfig.ipHelperText')}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label={t('admin.serverConfig.serverListenPort')}
          value={serverConfig.server_listen_port}
          onChange={(e) => handleServerConfigChange('server_listen_port', e.target.value)}
          helperText={t('admin.serverConfig.portHelperText')}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label={t('admin.serverConfig.serverAetTitle')}
          value={serverConfig.server_aet_title}
          onChange={(e) => handleServerConfigChange('server_aet_title', e.target.value)}
          helperText={t('admin.serverConfig.aetHelperText')}
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={Boolean(serverConfig.use_approval_workflow)}
              onChange={(e) => handleServerConfigChange('use_approval_workflow', e.target.checked)}
            />
          }
          label={t('admin.serverManagement.useApprovalWorkflow')}
          sx={{ mb: 3 }}
        />

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button
            onClick={testServerConfig}
            disabled={testLoading}
            startIcon={testLoading ? <CircularProgress size={20} /> : <CheckIcon />}
            className="button"
          >
            {t('admin.serverConfig.testConfiguration')}
          </Button>

          <Button
            onClick={saveServerConfig}
            disabled={saveLoading}
            startIcon={saveLoading ? <CircularProgress size={20} /> : null}
            className="button"
          >
            {t('admin.serverManagement.save')}
          </Button>
        </Box>

        {testResults && (
          <Alert severity={testResults.success ? 'success' : 'error'} sx={{ mb: 2 }}>
            <Typography variant="subtitle2">
              {testResults.success ? t('admin.serverConfig.configurationValid') : t('admin.serverConfig.configurationInvalid')}
            </Typography>
            
            {testResults.errors && testResults.errors.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight="bold">{t('admin.serverConfig.validationErrors')}:</Typography>
                <List dense>
                  {testResults.errors.map((error, index) => (
                    <ListItem key={index} sx={{ py: 0 }}>
                      <ListItemText primary={error} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {testResults.warnings && testResults.warnings.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight="bold">{t('admin.serverConfig.warnings')}:</Typography>
                <List dense>
                  {testResults.warnings.map((warning, index) => (
                    <ListItem key={index} sx={{ py: 0 }}>
                      <ListItemText primary={warning} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Alert>
        )}
      </Paper>

      {/* Server-Verwaltung */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PowerIcon color="primary" />
          <Typography variant="h6">{t('admin.serverConfig.serverManagement')}</Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('admin.serverConfig.serverManagementDescription')}
        </Typography>

        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {t('admin.serverConfig.serverManagementNote')}
          </Typography>
        </Alert>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            startIcon={<RestartIcon />}
            onClick={() => setRestartDialog(true)}
            disabled={restartLoading}
            className="button"
          >
            {t('admin.serverConfig.restartServerButton')}
          </Button>

          <Button
            startIcon={<ShutdownIcon />}
            onClick={() => setShutdownDialog(true)}
            disabled={shutdownLoading}
            className="button-delete"
          >
            {t('admin.serverConfig.shutdownServerButton')}
          </Button>

          <Button
            startIcon={<ScheduleIcon />}
            onClick={() => openScheduleDialog('restart')}
            className="button"
            sx={{ ml: 2 }}
          >
            Backend-Neustart planen
          </Button>

          <Button
            startIcon={<ScheduleIcon />}
            onClick={() => openScheduleDialog('shutdown')}
            className="button-delete"
          >
            Backend-Herunterfahren planen
          </Button>
        </Box>
      </Paper>

      {/* Restart Dialog */}
      <Dialog 
        open={restartDialog} 
        onClose={() => !restartLoading && setRestartDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RestartIcon color="primary" />
            {t('admin.serverConfig.restartConfirmTitle')}
          </Box>
        </DialogTitle>
        <DialogContent>
          {!restartLoading ? (
            <>
              <DialogContentText>
                {t('admin.serverConfig.restartConfirmMessage2')}
              </DialogContentText>
              <Alert severity="info" sx={{ mt: 2 }}>
                {t('admin.serverConfig.restartWarning')}
              </Alert>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              {restartStage === 'ready' ? (
                <>
                  <CheckIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: 'success.main' }}>
                    Server ist wieder verfügbar!
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Das Dialogfeld schließt sich automatisch in 5 Sekunden...
                  </Typography>
                </>
              ) : restartStage === 'token_invalid' ? (
                <>
                  <WarningIcon sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: 'warning.main' }}>
                    Session abgelaufen
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Sie werden zur Anmeldung weitergeleitet...
                  </Typography>
                </>
              ) : restartStage === 'timeout' ? (
                <>
                  <WarningIcon sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: 'warning.main' }}>
                    Restart dauert länger als erwartet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Bitte prüfen Sie manuell...
                  </Typography>
                </>
              ) : restartStage === 'error' ? (
                <>
                  <WarningIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: 'error.main' }}>
                    Fehler beim Server-Neustart
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Dialog schließt sich in 3 Sekunden...
                  </Typography>
                </>
              ) : (
                <>
                  <CircularProgress size={60} sx={{ mb: 2 }} />
                  <Typography variant="h6">
                    {restartStage === 'restarting' && 'Server wird neu gestartet...'}
                    {restartStage === 'checking' && 'Warte auf Server-Verfügbarkeit...'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {restartStage === 'restarting' && 'Bitte warten Sie einen Moment...'}
                    {restartStage === 'checking' && 'Der Server wird getestet bis er wieder verfügbar ist...'}
                  </Typography>
                  <LinearProgress sx={{ mt: 2 }} />
                </>
              )}
            </Box>
          )}
        </DialogContent>
        {!restartLoading && (
          <DialogActions>
            <Button onClick={() => setRestartDialog(false)} className="button">
              {t('common.cancel')}
            </Button>
            <Button onClick={restartServer} autoFocus className="button">
              {t('admin.serverConfig.startNow')}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Shutdown Dialog */}
      <Dialog 
        open={shutdownDialog} 
        onClose={() => !shutdownLoading && setShutdownDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            {t('admin.serverConfig.attention')}
          </Box>
        </DialogTitle>
        <DialogContent>
          {!shutdownLoading ? (
            <>
              <DialogContentText>
                {t('admin.serverConfig.shutdownConfirmMessage2')}
              </DialogContentText>
              <Alert severity="error" sx={{ mt: 2 }}>
                {t('admin.serverConfig.shutdownWarning')}
              </Alert>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h6">
                {t('admin.serverConfig.serverShuttingDown')}
              </Typography>
              <LinearProgress sx={{ mt: 2 }} />
            </Box>
          )}
        </DialogContent>
        {!shutdownLoading && (
          <DialogActions>
            <Button onClick={() => setShutdownDialog(false)} className="button">
              {t('common.cancel')}
            </Button>
            <Button onClick={shutdownServer} autoFocus className="button-delete">
              {t('admin.serverConfig.shutdownServerButton')}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Schedule Action Dialog */}
      <Dialog open={scheduleDialog.open} onClose={closeScheduleDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon color="primary" />
            {scheduleDialog.action === 'restart' ? 'Backend-Neustart planen' : 'Backend-Herunterfahren planen'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            {scheduleDialog.action === 'restart' 
              ? 'Planen Sie einen automatischen Backend-Neustart. Alle angemeldeten Benutzer werden benachrichtigt. Das Frontend bleibt verfügbar und verbindet sich automatisch wieder.'
              : 'Planen Sie ein automatisches Backend-Herunterfahren. Alle angemeldeten Benutzer werden benachrichtigt.'
            }
          </DialogContentText>
          
          <TextField
            autoFocus
            fullWidth
            label="Zeit in Minuten"
            type="number"
            value={scheduleDialog.minutes}
            onChange={(e) => setScheduleDialog(prev => ({ ...prev, minutes: parseInt(e.target.value) || 5 }))}
            inputProps={{ min: 1, max: 1440 }}
            helperText="Geben Sie die Anzahl Minuten bis zur Ausführung ein (1-1440)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeScheduleDialog} className="button">
            Abbrechen
          </Button>
          <Button 
            onClick={scheduleAction} 
            className={scheduleDialog.action === 'restart' ? 'button' : 'button-delete'}
            autoFocus
          >
            {scheduleDialog.action === 'restart' ? 'Neustart planen' : 'Herunterfahren planen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminConfig;
